import { useEffect } from 'react';

/**
 * This component disables React's error overlay and prevents
 * refreshing cycles caused by script errors in the admin dashboard.
 */
const ErrorDisabler = () => {
  useEffect(() => {
    // First, let's check if we're in the admin dashboard
    if (!window.location.pathname.includes('/admin-dashboard')) {
      return;
    }

    console.log('ErrorDisabler mounted - disabling React error overlay');

    // Set a flag to prevent maps loading loops
    window.shouldPreventMapRefresh = true;
    
    // Create an error handler to prevent script errors from propagating
    const errorHandler = (e) => {
      if (e.message === 'Script error.' || !e.filename) {
        console.log('Intercepted script error');
        e.stopPropagation();
        e.preventDefault();
        return true;
      }
      
      // Handle Google Maps specific errors
      if (e.message && (
          e.message.includes('google') || 
          e.message.includes('maps') || 
          e.message.includes('api') ||
          e.message.includes('script')
        )) {
        console.log('Intercepted maps-related error');
        e.stopPropagation();
        e.preventDefault();
        return true;
      }
    };

    // Add the error handler
    window.addEventListener('error', errorHandler, true);

    // Override console.error to prevent React from refreshing on certain errors
    const originalConsoleError = console.error;
    console.error = function(...args) {
      const errorString = String(args[0] || '');
      
      // Skip errors that cause refreshing
      if (errorString.includes('React will try to recreate this component tree') || 
          errorString.includes('Script error') ||
          errorString.includes('chunk') ||
          errorString.includes('maps') ||
          errorString.includes('Loading chunk') ||
          errorString.includes('google') ||
          errorString.includes('api')) {
        console.log('Suppressed console error:', errorString.substring(0, 100) + '...');
        return;
      }
      
      // Let other errors pass through
      originalConsoleError.apply(console, args);
    };

    // Handle unhandled rejections
    const rejectionHandler = (event) => {
      event.preventDefault();
      console.log('Suppressed unhandled rejection');
    };
    
    window.addEventListener('unhandledrejection', rejectionHandler);

    // Try to disable React's error overlay directly
    if (window.__REACT_ERROR_OVERLAY__) {
      window.__REACT_ERROR_OVERLAY__.handleRuntimeError = () => {};
      window.__REACT_ERROR_OVERLAY__.startReportingRuntimeErrors = () => {};
      window.__REACT_ERROR_OVERLAY__.dismissRuntimeErrors = () => {};
    }
    
    // Intercept map loading issues specifically
    const patchMapsLoading = () => {
      // Check if Google Maps is being loaded
      const originalCreateElement = document.createElement;
      document.createElement = function(tagName) {
        const element = originalCreateElement.call(document, tagName);
        
        // Intercept script elements being added to the DOM
        if (tagName.toLowerCase() === 'script') {
          const originalSetAttribute = element.setAttribute;
          element.setAttribute = function(name, value) {
            // If a Google Maps script is being loaded
            if (name === 'src' && value && value.includes('maps.googleapis.com')) {
              console.log('Intercepted Google Maps script loading');
              
              // Add error handling to the script
              element.onerror = function(e) {
                console.log('Prevented Google Maps script error from refreshing');
                e.stopPropagation();
                e.preventDefault();
                return true;
              };
            }
            
            return originalSetAttribute.call(this, name, value);
          };
        }
        
        return element;
      };
    };
    
    patchMapsLoading();

    // Clean up function
    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      console.error = originalConsoleError;
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default ErrorDisabler; 